import type {
  AuditList,
  AuditResultDetail,
  AuditRun,
  AuditSettings,
  ImportKind,
  ImportPreview,
  Mapping,
  ResultPage,
  ResultQuery,
  ReviewState,
  SavedMapping,
  ValidationReport,
} from "../types/api";
import { API_BASE, jsonBody, request } from "./client";

export function previewImport(kind: ImportKind, file: File, signal?: AbortSignal): Promise<ImportPreview> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("file", file);
  return request<ImportPreview>("/imports/preview", { method: "POST", body: form, signal });
}

export function repreviewImport(
  kind: ImportKind,
  importId: string,
  layout: { sheet?: string | null; headerRow?: number | null },
): Promise<ImportPreview> {
  const form = new FormData();
  form.append("kind", kind);
  form.append("import_id", importId);
  if (layout.sheet) form.append("sheet", layout.sheet);
  if (layout.headerRow) form.append("header_row", String(layout.headerRow));
  return request<ImportPreview>("/imports/preview", { method: "POST", body: form });
}

export function deleteImport(importId: string): Promise<void> {
  return request<void>(`/imports/${encodeURIComponent(importId)}`, { method: "DELETE" });
}

export interface AuditInputs {
  shipmentImportId: string;
  invoiceImportId: string;
  shipmentMapping: Mapping;
  invoiceMapping: Mapping;
  settings: AuditSettings;
}

export function validateImports(inputs: Omit<AuditInputs, "settings">, signal?: AbortSignal): Promise<ValidationReport> {
  return request<ValidationReport>("/imports/validate", {
    method: "POST",
    signal,
    ...jsonBody({
      shipment_import_id: inputs.shipmentImportId,
      invoice_import_id: inputs.invoiceImportId,
      shipment_mapping: inputs.shipmentMapping,
      invoice_mapping: inputs.invoiceMapping,
    }),
  });
}

export function createAudit(inputs: AuditInputs): Promise<AuditRun> {
  const form = new FormData();
  form.append("shipment_import_id", inputs.shipmentImportId);
  form.append("invoice_import_id", inputs.invoiceImportId);
  form.append("shipment_mapping", JSON.stringify(inputs.shipmentMapping));
  form.append("invoice_mapping", JSON.stringify(inputs.invoiceMapping));
  form.append("money_tolerance", inputs.settings.money_tolerance);
  form.append("weight_tolerance", inputs.settings.weight_tolerance);
  form.append("timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
  return request<AuditRun>("/audits", { method: "POST", body: form });
}

export function listAudits(options: { limit?: number; offset?: number } = {}, signal?: AbortSignal): Promise<AuditList> {
  const params = new URLSearchParams({ limit: String(options.limit ?? 20), offset: String(options.offset ?? 0) });
  return request<AuditList>(`/audits?${params}`, { signal });
}

export function getAudit(auditId: string, signal?: AbortSignal): Promise<AuditRun> {
  return request<AuditRun>(`/audits/${encodeURIComponent(auditId)}`, { signal });
}

export function deleteAudit(auditId: string): Promise<void> {
  return request<void>(`/audits/${encodeURIComponent(auditId)}`, { method: "DELETE" });
}

export function resultQueryString(query: ResultQuery): string {
  const params = new URLSearchParams();
  params.set("view", query.view);
  query.status.forEach((value) => params.append("status", value));
  query.reason.forEach((value) => params.append("reason", value));
  query.review_state.forEach((value) => params.append("review_state", value));
  if (query.q.trim()) params.set("q", query.q.trim());
  params.set("sort", query.sort);
  params.set("page", String(query.page));
  params.set("page_size", String(query.page_size));
  return params.toString();
}

export function listResults(auditId: string, query: ResultQuery, signal?: AbortSignal): Promise<ResultPage> {
  return request<ResultPage>(`/audits/${encodeURIComponent(auditId)}/results?${resultQueryString(query)}`, { signal });
}

export function getResult(auditId: string, resultId: string, signal?: AbortSignal): Promise<AuditResultDetail> {
  return request<AuditResultDetail>(
    `/audits/${encodeURIComponent(auditId)}/results/${encodeURIComponent(resultId)}`,
    { signal },
  );
}

export function reviewResult(
  auditId: string,
  resultId: string,
  change: { review_state?: ReviewState; note?: string | null },
): Promise<AuditResultDetail> {
  return request<AuditResultDetail>(
    `/audits/${encodeURIComponent(auditId)}/results/${encodeURIComponent(resultId)}`,
    { method: "PATCH", ...jsonBody(change) },
  );
}

export function exportUrl(auditId: string): string {
  return `${API_BASE}/audits/${encodeURIComponent(auditId)}/export`;
}

export function listMappings(kind: ImportKind): Promise<{ items: SavedMapping[] }> {
  return request<{ items: SavedMapping[] }>(`/mappings?type=${kind}`);
}

export function saveMapping(kind: ImportKind, name: string, mapping: Mapping): Promise<SavedMapping> {
  return request<SavedMapping>("/mappings", {
    method: "POST",
    ...jsonBody({ mapping_type: kind, name, mapping }),
  });
}
